import posthog from 'posthog-js';

// Type-safe event names
export const AnalyticsEvents = {
  HUB_VISIT: 'hub_visit',
  HEALTH_CHECK_STATUS: 'health_check_status',
  ERROR: 'error',
  PERFORMANCE_METRIC: 'performance_metric',
  SIGN_IN_ATTEMPTED: 'sign_in_attempted',
  SIGN_UP_SUCCESS: 'sign_up_success',
  // Anonymous play funnel (IMA-466)
  ANONYMOUS_SESSION_START: 'anonymous_session_start',
  ANONYMOUS_GAME_START: 'anonymous_game_start',
  ANONYMOUS_CONVERSION_CTA_SHOWN: 'anonymous_conversion_cta_shown',
  ANONYMOUS_CONVERSION_CTA_CLICKED: 'anonymous_conversion_cta_clicked',
} as const;

export type AnalyticsEvent = typeof AnalyticsEvents[keyof typeof AnalyticsEvents];

// Event property types
interface HubVisitProperties {
  referrer: string;
  timestamp: string;
  viewport: {
    width: number;
    height: number;
  };
  screen: {
    width: number;
    height: number;
  };
}

interface HealthCheckStatusProperties {
  api_status: 'ok' | 'error';
  database_status: 'connected' | 'disconnected';
  analytics_status: 'connected' | 'disconnected';
  response_time_ms: number;
}

interface ErrorProperties {
  error_type: string;
  error_message: string;
  component?: string;
  // Never include stack traces or sensitive data
}

interface PerformanceMetricProperties {
  metric_name: string;
  value: number;
  unit: string;
}

interface SignInAttemptedProperties {
  source: 'header' | 'modal' | 'page';
  method?: 'email' | 'google' | 'apple' | 'other';
}

interface SignUpSuccessProperties {
  source: 'header' | 'modal' | 'page';
  method: 'email' | 'google' | 'apple' | 'other';
}

// Analytics utility class
export class Analytics {
  private static isEnabled(): boolean {
    if (typeof window === 'undefined') {
      console.log('🚫 Analytics disabled: No window object');
      return false;
    }
    
    const isProduction = import.meta.env.MODE === 'production';
    const isPreview = import.meta.env.VITE_VERCEL_ENV === 'preview';
    const isDebugEnabled = import.meta.env.VITE_POSTHOG_DEBUG === 'true';
    const hasKey = !!import.meta.env.VITE_POSTHOG_KEY;
    
    console.log('📊 Analytics.isEnabled() check:', {
      isProduction,
      isPreview,
      isDebugEnabled,
      hasKey,
      posthogKey: import.meta.env.VITE_POSTHOG_KEY ? 'present' : 'missing',
      vercelEnv: import.meta.env.VITE_VERCEL_ENV,
      nodeEnv: import.meta.env.MODE,
    });
    
    const enabled = hasKey && (isProduction || isPreview || isDebugEnabled);
    
    console.log(`📊 Analytics ${enabled ? 'ENABLED ✅' : 'DISABLED ❌'}`);
    
    // Enable if: has key AND (in production/preview OR debug enabled)
    // PostHog will internally buffer events until initialization completes
    return enabled;
  }

  static trackHubVisit(properties: HubVisitProperties): void {
    if (this.isEnabled()) {
      posthog.capture(AnalyticsEvents.HUB_VISIT, properties);
    }
  }

  static trackHealthCheckStatus(properties: HealthCheckStatusProperties): void {
    if (this.isEnabled()) {
      posthog.capture(AnalyticsEvents.HEALTH_CHECK_STATUS, properties);
    }
  }

  static trackError(properties: ErrorProperties): void {
    if (this.isEnabled()) {
      // Sanitize error properties to ensure no sensitive data
      const sanitized: ErrorProperties = {
        error_type: properties.error_type.substring(0, 100),
        error_message: properties.error_message.substring(0, 200),
        component: properties.component?.substring(0, 50),
      };
      
      posthog.capture(AnalyticsEvents.ERROR, sanitized);
    }
  }

  static trackPerformance(properties: PerformanceMetricProperties): void {
    if (this.isEnabled()) {
      posthog.capture(AnalyticsEvents.PERFORMANCE_METRIC, properties);
    }
  }

  // Track Core Web Vitals
  static trackWebVitals(metric: { name: string; value: number }): void {
    if (this.isEnabled()) {
      this.trackPerformance({
        metric_name: `web_vital_${metric.name.toLowerCase()}`,
        value: Math.round(metric.value),
        unit: metric.name === 'CLS' ? 'score' : 'ms',
      });
    }
  }

  static trackSignInAttempted(properties: SignInAttemptedProperties): void {
    if (this.isEnabled()) {
      posthog.capture(AnalyticsEvents.SIGN_IN_ATTEMPTED, properties);
    }
  }

  static trackSignUpSuccess(properties: SignUpSuccessProperties): void {
    if (this.isEnabled()) {
      posthog.capture(AnalyticsEvents.SIGN_UP_SUCCESS, properties);
    }
  }

  // Opt-out functionality for privacy
  static optOut(): void {
    if (typeof window !== 'undefined' && posthog) {
      posthog.opt_out_capturing();
    }
  }

  static optIn(): void {
    if (typeof window !== 'undefined' && posthog) {
      posthog.opt_in_capturing();
    }
  }

  static isOptedOut(): boolean {
    if (typeof window !== 'undefined' && posthog) {
      return posthog.has_opted_out_capturing();
    }
    return false;
  }

  // Anonymous play funnel events (IMA-466)

  static trackAnonymousSessionStart(properties: { guestUserId: string; gameId?: string; challengeId?: string }): void {
    if (this.isEnabled()) {
      posthog.capture(AnalyticsEvents.ANONYMOUS_SESSION_START, properties);
    }
  }

  static trackAnonymousGameStart(properties: { gameId: string; challengeId: string }): void {
    if (this.isEnabled()) {
      posthog.capture(AnalyticsEvents.ANONYMOUS_GAME_START, properties);
    }
  }

  static trackAnonymousConversionCTAShown(properties: { gameId: string }): void {
    if (this.isEnabled()) {
      posthog.capture(AnalyticsEvents.ANONYMOUS_CONVERSION_CTA_SHOWN, properties);
    }
  }

  static trackAnonymousConversionCTAClicked(properties: { gameId: string }): void {
    if (this.isEnabled()) {
      posthog.capture(AnalyticsEvents.ANONYMOUS_CONVERSION_CTA_CLICKED, properties);
    }
  }
}